/**
 * The Receipts tab: a group's whole pile as a grid, with the ones nobody has
 * filed yet at the top.
 *
 * **A receipt may exist without an expense, and this screen is what that is
 * for.** The paper is in somebody's hand at the till and the phone is already
 * out; who was in on it and how it splits are questions for the evening. So
 * the upload here asks for no expense, the unfiled ones are surfaced first
 * because they are the ones with something left to do, and the two ways out of
 * that state are on every one of them: make the expense from it, or file it on
 * one that already exists.
 *
 * **The grid never downloads a full-size image.** Every tile is
 * `thumbnailUrl` — `?variant=thumb`, a 400px WebP of tens of kilobytes — and
 * `tileFor` in `src/lib/receipt-library.ts` is a function that *cannot* return
 * the original, so a missing thumbnail draws the file's icon rather than
 * reaching for hundreds of kilobytes to fill a stamp-sized box. The full-size
 * file is fetched in exactly one place, the viewer, one receipt at a time,
 * when somebody has asked to read the total.
 *
 * **The order is the server's.** `receipts_library` holds
 * `(group_id, unfiled first, newest first)` as an index, so a page is a range
 * on it; this screen groups those rows into headings and never re-sorts them,
 * because a client can only sort the rows it happens to have and the next page
 * would arrive out of order behind it.
 *
 * **Making an expense from a receipt prefills the date with the day the
 * photograph was taken**, not today. That is the whole point of filing in the
 * evening — see `expenseDateFor` — and when the write comes back, the receipt
 * is filed onto the expense that was just made, which is the same `PATCH` the
 * "file it on an existing one" control makes.
 */

import { useState } from 'react'
import type { LibraryReceipt } from '../api'
import { Thumbnail } from '../components/Receipts'
import { ReceiptUploader } from '../components/ReceiptUploader'
import { ReceiptViewer } from '../components/ReceiptViewer'
import { formatDay } from '../lib/format'
import { staggerStyle } from '../motion-react'
import {
  FILTERS,
  dayOf,
  emptyMessage,
  expenseDateFor,
  isUnfiled,
  nameOf,
  sectionsFor,
  type LibraryFilter,
} from '../lib/receipt-library'
import {
  useAttachLooseReceipt,
  useFileReceipt,
  useGroup,
  useGroupTransactions,
  useMe,
  useReceipt,
  useReceiptLibrary,
  useRemoveLibraryReceipt,
} from '../queries'
import type { Route } from '../routes'
import { AddExpense } from './AddExpense'

/** A 0/1 limit read the way `server/limits.ts` reads one: null is unlimited. */
function allowsUploads(limit: { value: number | null } | undefined): boolean {
  if (limit === undefined) return true
  return limit.value === null || limit.value > 0
}

export function ReceiptLibrary({
  groupId,
  myId,
  /** The receipt the URL is on, if any — the viewer is a route, not a flag. */
  openReceiptId,
  navigate,
}: {
  groupId: string
  myId: string
  openReceiptId: string | null
  navigate: (to: Route) => void
}) {
  const [filter, setFilter] = useState<LibraryFilter>('all')
  const [uploader, setUploader] = useState<string | null>(null)
  /** The receipt an expense is being made from, if the sheet is open. */
  const [makingFrom, setMakingFrom] = useState<LibraryReceipt | null>(null)

  const group = useGroup(groupId)
  const me = useMe()
  const transactions = useGroupTransactions(groupId)
  const library = useReceiptLibrary(groupId, filter, uploader)

  const attach = useAttachLooseReceipt(groupId)
  const fileIt = useFileReceipt(groupId)
  const remove = useRemoveLibraryReceipt(groupId)

  const members = group.data?.members ?? []
  const archived = group.data?.group.archivedAt != null
  const groupName = group.data?.group.name ?? 'this group'

  const receipts = library.data?.pages.flatMap((page) => page.receipts) ?? []
  const unfiled = library.data?.pages[0]?.unfiled ?? null
  const sections = sectionsFor(receipts)

  // The viewer's receipt comes out of the same list the grid draws, so opening
  // a tile costs nothing and closing it refetches nothing. The fetch below is
  // for the other way in — a link somebody was sent, a refresh, or a thumbnail
  // pressed under an expense whose receipt is four pages down — and it is
  // asked for only when the list is not already holding it.
  const loaded = openReceiptId === null ? null : receipts.find((row) => row.id === openReceiptId)
  const fetched = useReceipt(groupId, openReceiptId, loaded === undefined)
  const open = loaded ?? fetched.data?.receipt ?? null

  const busy = fileIt.isPending || remove.isPending
  const problem =
    fileIt.error?.message ??
    remove.error?.message ??
    (openReceiptId !== null && loaded === undefined ? (fetched.error?.message ?? null) : null)

  const show = (receiptId: string | null) =>
    navigate(
      receiptId === null
        ? { name: 'receipts', groupId }
        : { name: 'receipt', groupId, receiptId },
    )

  /** Files a receipt on an expense, or takes it off with `null`. */
  const file = (receiptId: string, transactionId: string | null) =>
    fileIt.mutate({ receiptId, transactionId })

  return (
    <>
      <p className="crumbs">
        <button
          type="button"
          className="button--quiet"
          onClick={() => navigate({ name: 'group', groupId })}
        >
          ← {groupName}
        </button>
      </p>

      <section className="card">
        <h2>Receipts</h2>
        {archived && (
          <p className="badge">Archived — nothing new can be added, and what is here stays.</p>
        )}

        <div className="library__filters">
          <fieldset className="library__pile">
            <legend className="visually-hidden">Which receipts</legend>
            {FILTERS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`tag${filter === option.id ? ' tag--on' : ''}`}
                aria-pressed={filter === option.id}
                onClick={() => setFilter(option.id)}
              >
                {option.label}
                {option.id === 'unfiled' && unfiled !== null && unfiled > 0 && (
                  <span className="library__count"> {unfiled}</span>
                )}
              </button>
            ))}
          </fieldset>

          <span className="library__by">
            <label htmlFor="library-uploader">Added by</label>
            <select
              id="library-uploader"
              value={uploader ?? ''}
              onChange={(event) => setUploader(event.target.value || null)}
            >
              <option value="">Anybody</option>
              {members.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.userId === myId ? `${member.displayName} (you)` : member.displayName}
                </option>
              ))}
            </select>
          </span>
        </div>

        {/*
          Uploading here files nothing: the receipt goes into the group's pile
          and waits. That is the flow this whole screen is for.
        */}
        <ReceiptUploader
          send={(prepared) => attach.mutateAsync(prepared)}
          storage={me.data?.storage ?? null}
          canUpload={allowsUploads(me.data?.limits.can_upload_attachments)}
          archived={archived}
          what={`${groupName}, to file later`}
        />
      </section>

      {makingFrom && group.data && (
        <section className="card">
          <h2>An expense for {nameOf(makingFrom)}</h2>
          <p className="muted note">
            Dated {formatDay(expenseDateFor(makingFrom))}, the day it was photographed. The
            receipt is filed on it as soon as it is written.
          </p>
          <AddExpense
            key={makingFrom.id}
            groupId={groupId}
            currency={group.data.group.currency}
            myId={myId}
            members={members}
            occurredOn={expenseDateFor(makingFrom)}
            fileReceiptId={makingFrom.id}
            // The receipt OCR's read, offered as a starting point on the
            // amount pad — never anything more than that. `AddExpense` puts
            // it there once, when the sheet first opens with nothing typed
            // into it, and a human still has to press "Add expense" for it to
            // become money anybody owes.
            suggestedAmountCents={
              makingFrom.ocrStatus === 'done' ? (makingFrom.ocrAmountCents ?? undefined) : undefined
            }
            onDone={() => setMakingFrom(null)}
          />
        </section>
      )}

      <section className="card">
        {library.isPending && <p className="muted">Fetching the pile…</p>}
        {library.isError && <p className="bad">{library.error.message}</p>}
        {problem && <p className="bad">{problem}</p>}
        {library.data && receipts.length === 0 && (
          <p className="muted">{emptyMessage(filter, uploader)}</p>
        )}

        {sections.map((section) => (
          <section key={section.key} className="month">
            <h3 className="month__heading">
              {section.heading}
              {section.unfiled && (
                <span className="muted note"> — waiting for an expense</span>
              )}
            </h3>
            <ul className="grid">
              {section.rows.map((receipt, index) => (
                <li
                  key={receipt.id}
                  className={`grid__cell${isUnfiled(receipt) ? ' grid__cell--unfiled' : ''}`}
                  style={staggerStyle(index)}
                >
                  <button
                    type="button"
                    className="grid__open"
                    onClick={() => show(receipt.id)}
                    aria-label={`Open ${nameOf(receipt)}`}
                  >
                    <Thumbnail receipt={receipt} />
                  </button>
                  <span className="grid__what">
                    {receipt.transaction && receipt.transaction.deletedAt === null ? (
                      receipt.transaction.description
                    ) : (
                      <span className="pill pill--unfiled">Not filed</span>
                    )}
                  </span>
                  {/* The day this tile is filed under — its expense's once it
                      has one, and the photograph's until then. The same
                      function the month heading above it was grouped by, so a
                      tile in August never shows a July date. */}
                  <span className="grid__when">{formatDay(dayOf(receipt))}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {library.hasNextPage && (
          <p className="actions">
            <button
              type="button"
              className="button--quiet"
              disabled={library.isFetchingNextPage}
              onClick={() => void library.fetchNextPage()}
            >
              {library.isFetchingNextPage ? 'Fetching…' : 'Older receipts'}
            </button>
          </p>
        )}
      </section>

      {open && group.data && (
        <ReceiptViewer
          receipt={open}
          currency={group.data.group.currency}
          expenses={transactions.data?.transactions ?? []}
          archived={archived}
          busy={busy}
          problem={problem}
          onClose={() => show(null)}
          onFile={(transactionId) => file(open.id, transactionId)}
          onMakeExpense={() => {
            setMakingFrom(open)
            show(null)
          }}
          onDelete={() => {
            // Closed first: what it was showing is about to stop existing, and
            // a viewer left open on a deleted receipt is a full-bleed 404.
            show(null)
            remove.mutate(open.id)
          }}
          onOpenExpense={() => navigate({ name: 'group', groupId })}
        />
      )}
    </>
  )
}
